import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button, Label } from "semantic-ui-react";
import { toast } from "react-toastify";

import { ITableAppRender } from "../../components/TableApp";
import TemplateTable from "../../components/TemplateTable";
import CadastroPessoa from "../../components/CadastroPessoa";

import RegionalManagerRepository, { IRegionalManagerListModel } from "../../Repositories/RegionalManagerRepository";
import { ISearch } from "../../components/Search";
import { ISearchResponse } from "../../interfaces/Search"
import EditarPessoa from "../../components/EditarPessoa";

import errors from "../../Utils/ExtractErrors";
import { GetUser } from "../../services/auth";
import { IFields } from "../../components/Search/FilterBuilder";
import UsuarioRepository from "../../Repositories/UsuarioRepository";
import ModalYesNo from "../../components/ModalYesNo";

const RegionalManagers: React.FC = () => {

    const [list, setList] = useState<IRegionalManagerListModel[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const refSearch = useRef<ISearch>({ searchString: "", type: "simples" });
    const refRecordsByPage = useRef<number>(20);
    const refPageNumber = useRef<string | number | undefined>(1);
    const refTotalRecords = useRef<number>(0);
    const refTotalPages = useRef<number>(1);

    const { role } = GetUser();

    const onChangeRecords = (value: number) => {
        refRecordsByPage.current = value;
        load();
    }

    const onPageChange = (pageNumber: string | number | undefined) => {
        refPageNumber.current = pageNumber;
        load();
    }

    const load = useCallback(async () => {

        setLoading(true);
        try {
            let response: ISearchResponse<IRegionalManagerListModel>;

            if (refSearch.current.type === "simples") {
                response = await RegionalManagerRepository.List({
                    search: refSearch.current.searchString,
                    pageNumber: refPageNumber.current,
                    recordsByPage: refRecordsByPage.current
                });
            } else {
                response = await RegionalManagerRepository.List({
                    filter: refSearch.current.searchString,
                    pageNumber: refPageNumber.current,
                    recordsByPage: refRecordsByPage.current
                });
            }

            refTotalRecords.current = response.totalRecords;
            refTotalPages.current = response.totalPages;

            setList(response.list);
        } catch (err) {
            if (err.response.status === 401) {
                window.location.href = "/";
            }

            const error = errors.getAllErrors(err.response.data);

            if (error !== undefined)
                toast(error, { position: "top-center", type: "error" });
        }
        setLoading(false);
    }, []);

    const onSearch = ({ searchString, type }: ISearch) => {
        refSearch.current.searchString = searchString;
        refSearch.current.type = type;

        load();
    }

    useEffect(() => { load() }, [load]);

    const setStatus = (status: number) => {
        switch (status) {
            case 0:
                return <Label ribbon icon="times" color="grey" content="INA" />
            case 1:
                return <Label ribbon icon="check" color="pink" content="ATI" />
            case 2:
                return <Label ribbon icon="clock" color="orange" content="PEN" />
            default:
                return <Label ribbon icon="user times" color="red" content="DEV" />

        }
    }

    const onReset = async (id: string) => {
        try {
            const response = await UsuarioRepository.Reset({ id });

            toast(`🔑 ${response.message}`, { position: "top-center", type: "success" });
            load();
        } catch (error) {
            return toast("❌ Erro ao resetar a senha.", { position: "top-center", type: "error" });
        }
    }
    const tableRender: ITableAppRender[] = [
        {
            header: "Status",
            content: (a: IRegionalManagerListModel) => setStatus(a.devedor === 1 ? 999 : a.status),
            textAlign: "left",
            width: 1
        },
        {
            header: "CPF",
            content: (a: IRegionalManagerListModel) => a.cpf,
            width: 2
        },
        {
            header: "Nome",
            content: (a: IRegionalManagerListModel) => a.nome,
            width: 6
        },
        {
            header: "Cidade",
            content: (a: IRegionalManagerListModel) => a.cidade,
            width: 2
        },
    ];

    const fields: IFields[] = [
        { label: "G. Regional", name: "Nome", type: "string", roles: ["Admin", "Operador"] },
        { label: "Cidade", name: "Cidade", type: "string", roles: ["Admin", "Operador", "GerenteR", "GerenteS"] },
        {
            label: "Status",
            name: "Status",
            type: "enum",
            values: [
                { key: "INA", text: "INA", value: 0, label: { icon: "times", color: "grey", content: "INA" } },
                { key: "ATI", text: "ATI", value: 1, label: { icon: "check", color: "pink", content: "ATI" } },
                { key: "PEN", text: "PEN", value: 2, label: { icon: "clock", color: "orange", content: "PEN" } }
            ],
            roles: ["Admin", "Operador"]
        },
        {
            label: "Devedor",
            name: "Devedor",
            type: "enum",
            values: [
                { key: "NÃO", text: "NÃO", value: 0, label: { icon: "user", color: "green", content: "NDV" } },
                { key: "SIM", text: "SIM", value: 1, label: { icon: "user times", color: "red", content: "DEV" } },
            ],
            roles: ["Admin", "Operador"]
        }
    ];

    return (
        <TemplateTable
            title="Gerentes regionais"
            icon="user secret"
            insert={role === "Admin" || "Operador" ? <CadastroPessoa url="/gerenteregional" status="1" onSuccess={load} /> : undefined}

            searchPlaceHolder={"Nome"}
            onSearch={onSearch}
            filterFields={fields}

            tableLoading={loading}
            tableRender={tableRender}
            tableList={list}
            tableActions={(data: IRegionalManagerListModel, index: number) => [
                <EditarPessoa id={data.id || ""} url={`/gerenteregional/${data.id}`} onSuccess={load} />,
                <ModalYesNo
                    trigger={
                        role === "Admin" &&
                        <Button
                            icon="refresh"
                            color="green"
                        />
                    }
                    header="Resetar senha"
                    content={`Deseja realmente resetar a senha do usuario ${data.nome}`}
                    onSuccess={() => onReset(data.id || "")}
                />
            ]}
            tablePagination={{
                currentPage: refPageNumber.current,
                totalRecords: refTotalRecords.current,
                totalPages: refTotalPages.current,
                onPageChange: onPageChange
            }}
            tableOnChangeRecords={onChangeRecords}
        />
    );
};

export default RegionalManagers;
